// 2D geometry helpers for feature extraction.
//
// IMPORTANT: angles computed here are used ONLY internally to classify
// patterns and derive categorical bands. They are never surfaced to the user as
// clinical joint-angle values (per product/regulatory design).

import type { Landmark } from "../pose/landmarks";

export interface Vec {
  x: number;
  y: number;
}

export function vec(a: Landmark, b: Landmark): Vec {
  return { x: b.x - a.x, y: b.y - a.y };
}

/** Interior angle at vertex b formed by a-b-c, in degrees (0..180). */
export function angleAt(a: Landmark, b: Landmark, c: Landmark): number {
  const u = vec(b, a);
  const v = vec(b, c);
  const dot = u.x * v.x + u.y * v.y;
  const mag = Math.hypot(u.x, u.y) * Math.hypot(v.x, v.y);
  if (mag === 0) return 0;
  const cos = Math.min(1, Math.max(-1, dot / mag));
  return (Math.acos(cos) * 180) / Math.PI;
}

/** Angle of a vector from the vertical axis, in degrees (0 = perfectly upright). */
export function angleFromVertical(v: Vec): number {
  // Vertical reference is (0,1) in image space (y grows downward).
  const mag = Math.hypot(v.x, v.y);
  if (mag === 0) return 0;
  const cos = Math.min(1, Math.max(-1, Math.abs(v.y) / mag));
  return (Math.acos(cos) * 180) / Math.PI;
}

/**
 * Signed perpendicular distance from point p to the line through a→b.
 * Sign indicates which side of the line p lies on (used for medial/lateral
 * knee deviation, i.e. valgus vs varus tendency).
 */
export function signedLineDistance(p: Landmark, a: Landmark, b: Landmark): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return 0;
  // Cross product of (b-a) x (p-a) / |b-a|
  return ((p.x - a.x) * dy - (p.y - a.y) * dx) / len;
}

/** Signed angle (deg) from vector u to vector v, in [-180, 180]. */
export function signedAngleDeg(u: Vec, v: Vec): number {
  const dot = u.x * v.x + u.y * v.y;
  const cross = u.x * v.y - u.y * v.x;
  return (Math.atan2(cross, dot) * 180) / Math.PI;
}

export function percentile(xs: number[], p: number): number {
  if (xs.length === 0) return 0;
  const sorted = [...xs].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.round((p / 100) * (sorted.length - 1))));
  return sorted[idx];
}

export function clamp(x: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, x));
}

/** Symmetry index: 0 = identical, grows with asymmetry (percent). */
export function symmetryIndex(l: number, r: number): number {
  const denom = 0.5 * (Math.abs(l) + Math.abs(r));
  if (denom === 0) return 0;
  return (Math.abs(l - r) / denom) * 100;
}

/**
 * Normalized dimensionless jerk of a 1-D trajectory — a smoothness proxy.
 * Lower = smoother. Computed as mean |3rd derivative| scaled by duration and
 * range, so it is comparable across clips.
 */
export function normalizedJerk(xs: number[], dtMs: number): number {
  if (xs.length < 4) return 0;
  const dt = dtMs / 1000;
  const range = Math.max(...xs) - Math.min(...xs) || 1;
  let acc = 0;
  let n = 0;
  for (let i = 3; i < xs.length; i++) {
    const j = (xs[i] - 3 * xs[i - 1] + 3 * xs[i - 2] - xs[i - 3]) / (dt * dt * dt);
    acc += j * j;
    n++;
  }
  if (n === 0) return 0;
  const meanSq = acc / n;
  // Scale to a dimensionless, bounded-ish value.
  return Math.sqrt(meanSq) * (dt * dt * dt) / range;
}
